import lodash from 'lodash';
import { useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import {
  Checkbox,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';
import ClaimCommunicationInfoImg from '../../../assets/img/info-icon.svg';
import { HeaderContent } from '../../../components/HeaderContent';
import { PlanStatus } from '../../../domain/plan/PlanStatus';
import { Project } from '../../../domain/project/Project';
import { ProjectPlans, defaultNameSubstitutions, validExecutionDate } from '../../../domain/project/ProjectPlans';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import { ContainerInputs } from '../shared';
import { EditableContent } from './EditableContent';

type ProjectSettingsPlansProps = {
  project: Project;
  isDirtyState: (...args: any[]) => any;
};

export const ProjectSettingsPlans = ({ project, isDirtyState }: ProjectSettingsPlansProps) => {
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { run: editProject, success } = useUpdateProject({ project });
  const [dirtyState, setDirtyState] = useState<boolean>(false);
  const [plansConfig, setPlansConfig] = useState<ProjectPlans>(project.plans);

  useEffect(() => {
    if (project) {
      setPlansConfig(project.plans);
    }
  }, [project]);

  useEffect(() => {
    if (project) {
      const newValues = {
        label: plansConfig.label,
        status: plansConfig.status,
        execution_date: plansConfig.execution_date,
        hide_completed_stops: plansConfig.hide_completed_stops,
      };

      const originalValues = project.plans
        ? {
            label: project.plans.label,
            status: project.plans.status,
            execution_date: project.plans.execution_date,
            hide_completed_stops: project.plans.hide_completed_stops,
          }
        : undefined;

      if (!lodash.isEqual(newValues, originalValues)) {
        setDirtyState(true);
      } else {
        setDirtyState(false);
      }
    }
  }, [plansConfig, project]);

  useEffect(() => {
    if (success) {
      setDirtyState(false);
    }
  }, [success]);

  useEffect(() => {
    isDirtyState(dirtyState);
  }, [dirtyState, isDirtyState]);

  const handleEditProject = () => {
    if (dirtyState) {
      editProject({
        ...(plansConfig ? { plans: plansConfig } : {}),
      });
    }
  };

  if (!project) return null;

  const canUpdateProject = userCredentials?.projects.test(project.id, `can_update_project`) ?? false;

  return (
    <>
      <Grid container direction="column">
        <Grid
          container
          direction="column"
          style={{
            padding: `11px 20px`,
            backgroundColor: RoutalPalette.neutral02n,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
          wrap="nowrap"
        >
          <Grid container direction="row" wrap="nowrap">
            <HeaderContent
              titleIntlId="projects.settings.plans.title"
              article={{
                intlId: `projects.settings.plans.article`,
                link: retrieveDoc({ supportPage: `SMSummary`, language: user?.language.value }),
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="column"
        style={{
          padding: `16px 22px`,
        }}
      >
        <Grid item>
          <VSpacer large />

          <Grid item>
            <Text variant="h6" intlId="projects.settings.plans.configuration" />
          </Grid>

          <VSpacer medium />

          <ContainerInputs>
            {canUpdateProject ? (
              <>
                <Grid container direction="row">
                  <Text variant="label">
                    {`${intl.formatMessage({
                      id: `projects.settings.plans.label`,
                    })}: ${ProjectPlans.labelTemplateToValue({
                      template: plansConfig.label,
                      date: new Date(),
                      language: user?.language.value,
                    })}`}
                  </Text>
                </Grid>

                <VSpacer small />

                <EditableContent
                  id="default-plan-label-editable-content"
                  value={plansConfig.label}
                  substitutions={defaultNameSubstitutions.map((substitution) => ({
                    id: substitution,
                    label: intl.formatMessage({
                      id: `projects.settings.plans.label.substitutions.${substitution}`,
                    }),
                  }))}
                  placeholder={`${intl.formatMessage({
                    id: `projects.settings.plans.label`,
                  })} {{day}}/{{month}}/{{year}}`}
                  onValueChange={(value: string) => {
                    const plansConfigClone = ProjectPlans.create(plansConfig.getValue());
                    plansConfigClone.label = value;
                    setPlansConfig(plansConfigClone);
                  }}
                />

                <VSpacer small />

                {/* <Text
                  variant="label"
                  style={{ fontSize: `11px`, textAlign: `end` }}
                  intlId="projects.settings.plans.label.result"
                  intlValues={{
                    result: `${ProjectPlans.labelTemplateToValue({
                      template: plansConfig.label,
                      date: new Date(),
                      language: user?.language.value,
                    })}`,
                  }}
                /> */}
              </>
            ) : (
              <Grid item>
                <Text variant="label" intlId="projects.settings.plans.label" />
                <VSpacer small />
                <Text style={{ paddingBottom: `8px` }}>{plansConfig.label}</Text>
              </Grid>
            )}

            <VSpacer medium />

            {canUpdateProject ? (
              <>
                <Text variant="label" intlId="projects.settings.plans.status" />

                <VSpacer small />

                <RoutalSelect
                  id="select-status"
                  popoverProps={{
                    options: ([`draft`, `planning`] as PlanStatus[]).map(
                      (status): RoutalSelectMultiSelectOption => ({
                        key: status,
                        content: (
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            alignItems="center"
                            style={{ overflow: `hidden` }}
                          >
                            <Text
                              tagName="span"
                              style={{
                                textAlign: `justify`,
                                whiteSpace: `nowrap`,
                                overflow: `hidden`,
                                width: `100%`,
                                textOverflow: `ellipsis`,
                                cursor: `pointer`,
                              }}
                              intlId={`plan_settings_dialog.status.${status}`}
                            />
                          </Grid>
                        ),
                        onClick: () => {
                          const plansConfigClone = ProjectPlans.create(plansConfig.getValue());
                          plansConfigClone.status = status;
                          setPlansConfig(plansConfigClone);
                        },
                      })
                    ),
                  }}
                >
                  <Text
                    style={{
                      cursor: `pointer`,
                      width: `100%`,
                      textAlign: `justify`,
                    }}
                    intlId={`plan_settings_dialog.status.${plansConfig.status}`}
                  />
                </RoutalSelect>
              </>
            ) : (
              <Grid item>
                <Text variant="label" intlId="projects.settings.plans.status" />
                <VSpacer small />
                <Text style={{ paddingBottom: `8px` }}>{plansConfig.status}</Text>
              </Grid>
            )}

            <VSpacer medium />

            {canUpdateProject ? (
              <>
                <Grid container>
                  <Text variant="label" intlId="projects.settings.plans.execution_date" />
                  <RoutalTooltip
                    placement="right"
                    title={intl.formatMessage({ id: `projects.settings.plans.execution_date.tooltip` })}
                  >
                    <img
                      style={{ marginLeft: `8px` }}
                      src={ClaimCommunicationInfoImg}
                      alt=""
                      height="16"
                      width="16"
                      draggable="false"
                    />
                  </RoutalTooltip>
                </Grid>

                <VSpacer small />

                <RoutalSelect
                  id="select-distance"
                  popoverProps={{
                    options: validExecutionDate.map(
                      (start): RoutalSelectMultiSelectOption => ({
                        key: start,
                        content: (
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            alignItems="center"
                            style={{ overflow: `hidden` }}
                          >
                            <Text
                              tagName="span"
                              style={{
                                textAlign: `justify`,
                                whiteSpace: `nowrap`,
                                overflow: `hidden`,
                                width: `100%`,
                                textOverflow: `ellipsis`,
                                cursor: `pointer`,
                              }}
                              intlId={`projects.settings.plans.execution_date.${start}`}
                            />
                          </Grid>
                        ),
                        onClick: () => {
                          const plansConfigClone = ProjectPlans.create(plansConfig.getValue());
                          plansConfigClone.execution_date = start;
                          setPlansConfig(plansConfigClone);
                        },
                      })
                    ),
                  }}
                >
                  <Text
                    style={{
                      cursor: `pointer`,
                      width: `100%`,
                      textAlign: `justify`,
                    }}
                    intlId={`projects.settings.plans.execution_date.${plansConfig.execution_date}`}
                  />
                </RoutalSelect>
              </>
            ) : (
              <Grid item>
                <Text variant="label" intlId="projects.settings.plans.execution_date" />
                <VSpacer small />
                <Text
                  style={{ paddingBottom: `8px` }}
                  intlId={`projects.settings.plans.execution_date.${plansConfig.execution_date}`}
                />
              </Grid>
            )}
          </ContainerInputs>

          <VSpacer large />

          <Grid item>
            <Text variant="h6" intlId="projects.settings.plans.completed_stops" />
          </Grid>

          <VSpacer medium />

          <ContainerInputs direction="row" wrap="nowrap">
            <Checkbox
              checked={canUpdateProject ? plansConfig.hide_completed_stops : false}
              onChange={() => {
                const plansConfigClone = ProjectPlans.create(plansConfig.getValue());
                plansConfigClone.hide_completed_stops = !plansConfig.hide_completed_stops;
                setPlansConfig(plansConfigClone);
              }}
              disabled={!canUpdateProject}
            />

            <Text
              variant="body"
              style={{ marginLeft: `4px`, alignSelf: `center` }}
              intlId="projects.settings.plans.completed_stops.description"

              // color: var(--Primary-80, #072120);

              // /* Body */
              // font-family: Geist;
              // font-size: 13px;
              // font-style: normal;
              // font-weight: 400;
              // line-height: normal;
            />
          </ContainerInputs>
        </Grid>

        <Grid item style={{ position: `fixed`, bottom: `40px`, right: `40px` }}>
          <Button
            id="project-settings-plans-save-changes"
            variant={dirtyState ? `default` : `flat`}
            onClick={handleEditProject}
            style={{ maxWidth: `200px` }}
            intlId="settings.buttonupdate"
          />
        </Grid>
      </Grid>
    </>
  );
};
